import React, { useState, useEffect } from 'react';
import { Editor, EditorState, convertToRaw, convertFromRaw, Modifier, CompositeDecorator, SelectionState, getDefaultKeyBinding } from 'draft-js';
import 'draft-js/dist/Draft.css';
import './App.css';

// Custom component to render generated text
const GeneratedSpan = (props) => {
  return <span className="generated-text">{props.children}</span>;
};

// Create a decorator for generated text
const generateDecorator = () => {
  return new CompositeDecorator([
    {
      strategy: (contentBlock, callback, contentState) => {
        contentBlock.findEntityRanges(
          (character) => {
            const entityKey = character.getEntity();
            return (
              entityKey !== null &&
              contentState.getEntity(entityKey).getType() === 'GENERATED'
            );
          },
          callback
        );
      },
      component: GeneratedSpan,
    },
  ]);
};

function App() {
  const [apiKey, setApiKey] = useState('');
  const [editorState, setEditorState] = useState(
    EditorState.createEmpty(generateDecorator())
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(1);
  const [stopTokens, setStopTokens] = useState([]);
  const [currentStopToken, setCurrentStopToken] = useState('');
  const [maxTokens, setMaxTokens] = useState(100);
  const [abortController, setAbortController] = useState(null);
  const [showApiKeyPopup, setShowApiKeyPopup] = useState(true);

  useEffect(() => {
    const savedContent = localStorage.getItem('draftContent');
    if (savedContent) {
      const content = convertFromRaw(JSON.parse(savedContent));
      setEditorState(EditorState.createWithContent(content, generateDecorator()));
    }
  }, []);

  const saveContent = (content) => {
    localStorage.setItem('draftContent', JSON.stringify(convertToRaw(content)));
  };

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);
    saveContent(newEditorState.getCurrentContent());
  };

  const hasGeneratedContent = (editorState) => {
    const content = editorState.getCurrentContent();
    return content.getBlockMap().some(block => 
      block.getCharacterList().some(char => char.getEntity() !== null)
    );
  };

  const handleStopTokenKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (currentStopToken.trim() !== '') {
        setStopTokens([...stopTokens, currentStopToken.trim()]);
        setCurrentStopToken('');
      }
    }
  };

  const removeStopToken = (index) => {
    setStopTokens(stopTokens.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const content = editorState.getCurrentContent();
    const prompt = content.getPlainText();

    const controller = new AbortController();
    setAbortController(controller);

    try {
      const result = await fetch("https://openrouter.ai/api/v1/completions", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiKey}`,
          "HTTP-Referer": window.location.origin,
          "X-Title": "Base Model Playground",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          "model": "meta-llama/llama-3.1-405b",
          "prompt": prompt,
          "max_tokens": parseInt(maxTokens),
          "temperature": parseFloat(temperature),
          "top_p": parseFloat(topP),
          "stop": stopTokens.length > 0 ? stopTokens : undefined,
          "stream": true
        }),
        signal: controller.signal
      });

      if (!result.ok) {
        const errorData = await result.json();
        throw new Error(errorData.error?.message || 'An error occurred');
      }

      const reader = result.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        const parsedLines = lines
          .map((line) => line.replace(/^data: /, '').trim())
          .filter((line) => line !== '' && line !== '[DONE]')
          .map((line) => {
            try {
              return JSON.parse(line);
            } catch (e) {
              console.error('Error parsing JSON:', line);
              return null;
            }
          })
          .filter(line => line !== null);

        for (const parsedLine of parsedLines) {
          if (parsedLine.error) {
            throw new Error(parsedLine.error.message);
          }
          const { choices } = parsedLine;
          const { text } = choices[0];
          if (text) {
            setEditorState((currentEditorState) => {
              const currentContent = currentEditorState.getCurrentContent();
              const blockMap = currentContent.getBlockMap();
              const lastBlock = blockMap.last();
              const lastBlockKey = lastBlock.getKey();
              const lastBlockLength = lastBlock.getLength();
              
              const contentWithEntity = currentContent.createEntity(
                'GENERATED',
                'MUTABLE'
              );
              const entityKey = contentWithEntity.getLastCreatedEntityKey();

              const newSelection = currentEditorState.getSelection().merge({
                anchorKey: lastBlockKey,
                anchorOffset: lastBlockLength,
                focusKey: lastBlockKey,
                focusOffset: lastBlockLength,
              });

              const newContent = Modifier.insertText(
                contentWithEntity,
                newSelection,
                text,
                null,
                entityKey
              );

              const newEditorState = EditorState.push(currentEditorState, newContent, 'insert-characters');
              return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter());
            });
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Generation stopped by user.');
      } else {
        console.error('Error:', error);
        if (error.message.includes('Too Many Requests')) {
          setError('Too many requests. Please wait a moment before trying again.');
        } else {
          setError(`An error occurred: ${error.message}`);
        }
      }
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  };

  const handleStopGeneration = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  const handleRemoveGeneration = () => {
    const content = editorState.getCurrentContent();
    const blocks = content.getBlocksAsArray();
    let newContent = content;
    let removedAny = false;
    let regenerationPoint = null;

    for (let i = blocks.length - 1; i >= 0; i--) {
      const block = blocks[i];
      const chars = block.getCharacterList();
      let start = null;
      let end = null;

      for (let j = chars.size - 1; j >= 0; j--) {
        const char = chars.get(j);
        const entityKey = char.getEntity();
        if (entityKey !== null && content.getEntity(entityKey).getType() === 'GENERATED') {
          if (end === null) end = j + 1;
          start = j;
        } else if (end !== null) {
          break;
        }
      }

      if (start !== null && end !== null) {
        newContent = Modifier.removeRange(
          newContent,
          new SelectionState({
            anchorKey: block.getKey(),
            anchorOffset: start,
            focusKey: block.getKey(),
            focusOffset: end,
          }),
          'backward'
        );
        removedAny = true;
        regenerationPoint = {
          blockKey: block.getKey(),
          offset: start
        };
        break;  // Remove only the last generated segment
      }
    }

    if (removedAny) {
      const newEditorState = EditorState.push(editorState, newContent, 'remove-range');
      setEditorState(EditorState.forceSelection(newEditorState, newContent.getSelectionAfter()));
      
      // Trigger regeneration
      if (regenerationPoint) {
        handleRegenerate(regenerationPoint);
      }
    }
  };

  const handleRegenerate = async (regenerationPoint) => {
    setIsLoading(true);
    setError('');

    const content = editorState.getCurrentContent();
    const blocks = content.getBlocksAsArray();
    let prompt = '';

    // Construct prompt from content up to the regeneration point
    for (const block of blocks) {
      if (block.getKey() === regenerationPoint.blockKey) {
        prompt += block.getText().slice(0, regenerationPoint.offset);
        break;
      } else {
        prompt += block.getText() + '\n';
      }
    }

    const controller = new AbortController();
    setAbortController(controller);

    try {
      const result = await fetch("https://openrouter.ai/api/v1/completions", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiKey}`,
          "HTTP-Referer": window.location.origin,
          "X-Title": "Base Model Playground",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          "model": "https://openrouter.ai/models/meta-llama/llama-3.1-405b",
          "prompt": prompt,
          "max_tokens": parseInt(maxTokens),
          "temperature": parseFloat(temperature),
          "top_p": parseFloat(topP),
          "stop": stopTokens.length > 0 ? stopTokens : undefined,
          "stream": true
        }),
        signal: controller.signal
      });

      if (!result.ok) {
        const errorData = await result.json();
        throw new Error(errorData.error?.message || 'An error occurred');
      }

      const reader = result.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        const parsedLines = lines
          .map((line) => line.replace(/^data: /, '').trim())
          .filter((line) => line !== '' && line !== '[DONE]')
          .map((line) => {
            try {
              return JSON.parse(line);
            } catch (e) {
              console.error('Error parsing JSON:', line);
              return null;
            }
          })
          .filter(line => line !== null);

        for (const parsedLine of parsedLines) {
          if (parsedLine.error) {
            throw new Error(parsedLine.error.message);
          }
          const { choices } = parsedLine;
          const { text } = choices[0];
          if (text) {
            setEditorState((currentEditorState) => {
              const currentContent = currentEditorState.getCurrentContent();
              const blockMap = currentContent.getBlockMap();
              const lastBlock = blockMap.last();
              const lastBlockKey = lastBlock.getKey();
              const lastBlockLength = lastBlock.getLength();
              
              const contentWithEntity = currentContent.createEntity(
                'GENERATED',
                'MUTABLE'
              );
              const entityKey = contentWithEntity.getLastCreatedEntityKey();

              const newSelection = currentEditorState.getSelection().merge({
                anchorKey: lastBlockKey,
                anchorOffset: lastBlockLength,
                focusKey: lastBlockKey,
                focusOffset: lastBlockLength,
              });

              const newContent = Modifier.insertText(
                contentWithEntity,
                newSelection,
                text,
                null,
                entityKey
              );

              const newEditorState = EditorState.push(currentEditorState, newContent, 'insert-characters');
              return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter());
            });
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Generation stopped by user.');
      } else {
        console.error('Error:', error);
        if (error.message.includes('Too Many Requests')) {
          setError('Too many requests. Please wait a moment before trying again.');
        } else {
          setError(`An error occurred: ${error.message}`);
        }
      }
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  };

  const handleApiKeySubmit = (e) => {
    e.preventDefault();
    if (apiKey.trim() !== '') {
      setShowApiKeyPopup(false);
    }
  };

  const handleKeyCommand = (command, editorState) => {
    if (command === 'generate') {
      handleSubmit(new Event('submit'));
      return 'handled';
    }
    return 'not-handled';
  };

  const keyBindingFn = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      return 'generate';
    }
    return getDefaultKeyBinding(e);
  };

  return (
    <div className="App">
      <a href="https://x.com/mattshumer_" target="_blank" rel="noopener noreferrer" className="credit-link">
        Created by @mattshumer_
      </a>
      {showApiKeyPopup && (
        <div className="api-key-popup-overlay">
          <div className="api-key-popup">
            <h2>Welcome to Base Model Playground</h2>
            <p>Please enter your OpenRouter API key to get started:</p>
            <form onSubmit={handleApiKeySubmit}>
              <input
                type="password"
                placeholder="Enter your OpenRouter API key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                required
              />
              <button type="submit" className="primary-button">Submit</button>
            </form>
          </div>
        </div>
      )}
      <div className="playground">
        <div className="editor-panel">
          <h1>Base Model Playground</h1>
          <div className="editor-container">
            <Editor
              editorState={editorState}
              onChange={handleEditorChange}
              placeholder="Enter your prompt here"
              handleKeyCommand={handleKeyCommand}
              keyBindingFn={keyBindingFn}
            />
          </div>
          <div className="button-container">
            <button type="button" onClick={handleSubmit} disabled={isLoading || !apiKey || !editorState.getCurrentContent().hasText()} className="primary-button">
              {isLoading ? 'Generating...' : 'Generate'}
            </button>
            {isLoading && (
              <button type="button" onClick={handleStopGeneration} className="stop-button">
                Stop Generating
              </button>
            )}
            {!isLoading && hasGeneratedContent(editorState) && (
              <button type="button" onClick={handleRemoveGeneration} className="remove-button">
                Regenerate
              </button>
            )}
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
        <div className="settings-panel">
          <h2>Settings</h2>
          <div className="slider-container">
            <label>
              <span>Temperature: {temperature}</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
              />
            </label>
          </div>
          <div className="slider-container">
            <label>
              <span>Top P: {topP}</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={topP}
                onChange={(e) => setTopP(e.target.value)}
              />
            </label>
          </div>
          <div className="slider-container">
            <label>
              <span>Max Tokens: {maxTokens}</span>
              <input
                type="range"
                min="1"
                max="8192"
                step="1"
                value={maxTokens}
                onChange={(e) => setMaxTokens(e.target.value)}
              />
            </label>
          </div>
          <div className="stop-tokens-container">
            <label>
              Stop Tokens:
              <input
                type="text"
                value={currentStopToken}
                onChange={(e) => setCurrentStopToken(e.target.value)}
                onKeyDown={handleStopTokenKeyDown}
                placeholder="Press Tab to add"
              />
            </label>
            <div className="stop-tokens-list">
              {stopTokens.map((token, index) => (
                <span key={index} className="stop-token">
                  {token}
                  <button type="button" onClick={() => removeStopToken(index)}>×</button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
